import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { SelectChangeEvent } from 'primeng/select';
import { MarketingBanner } from '../../../models/marketing-banner.model';
import { ProgramTypeName } from '../../../models/program-type-name.model';
import { OsmosisService } from '../../services/osmosis.service';
import { MarketingBannerHistoryComponent } from '../marketing-banner-history/marketing-banner-history.component';
import { CommonSharedModule } from '../../common/shared.module';

@Component({
  selector: 'app-marketing-banner',
  templateUrl: './marketing-banner.component.html',
  styleUrls: ['./marketing-banner.component.scss'],
  standalone: true,
  imports: [MarketingBannerHistoryComponent, CommonSharedModule]
})
export class MarketingBannerComponent implements OnInit {
  @ViewChild('marketingBannerHistory', { static: false })
  marketingBannerHistory!: MarketingBannerHistoryComponent;
  marketingBannerForm!: FormGroup;

  loading = false;
  bannerText = '';
  isActive = false;
  startStopText = 'Loading...';
  currentBannerMsg!: string;
  currentBannerLink!: string;
  currentBanner!: MarketingBanner;
  programTypes!: ProgramTypeName[];

  constructor(
    private formBuilder: FormBuilder,
    private osmService: OsmosisService
  ) {}

  ngOnInit(): void {
    this.onFromGroup();
    this.onLoadProgramType();
    this.marketingBannerForm.controls['programType'].setValue({
      programTypeName: this.programTypes[0].programTypeName,
      programTypeId: this.programTypes[0].programTypeId
    });
    this.refreshBanner(1);
  }

  onFromGroup(): void {
    this.marketingBannerForm = this.formBuilder.group({
      bannerMessage: [''],
      linkToLearnMore: [''],
      programType: ['']
    });
  }

  getBannerStatus(programTypeId): void {
    this.loading = true;
    this.osmService
      .isFeatureEnabled('MarketingBanner', programTypeId)
      .subscribe(
        response => {
          this.loading = false;
          this.isActive = response;
          this.updateStartStopText(response);
        },
        () => (this.loading = false)
      );
  }

  getBanner(programTypeId): void {
    this.loading = true;
    this.osmService.getMarketingBanner(programTypeId).subscribe(
      response => {
        this.loading = false;
        this.currentBanner = response;
        if (response) {
          this.marketingBannerForm.controls['bannerMessage'].setValue(
            response.description
          );
          this.marketingBannerForm.controls['linkToLearnMore'].setValue(
            response.linkToLearnMore
          );
          this.currentBannerMsg = response.description;
          this.currentBannerLink = response.linkToLearnMore;
        }
      },
      () => (this.loading = false)
    );
  }

  refreshBanner(programTypeId): void {
    this.getBannerStatus(programTypeId);
    this.getBanner(programTypeId);
    if (this.marketingBannerHistory) {
      this.marketingBannerHistory.getMarketingBannerHistory(programTypeId);
    }
  }

  startStopBanner(): void {
    this.loading = true;
    const programType = this.marketingBannerForm.controls['programType'].value;
    this.osmService
      .updateFeature(
        'MarketingBanner',
        !this.isActive,
        programType.programTypeId
      )
      .subscribe(
        () => {
          this.loading = false;
          const bannerMessage =
            this.marketingBannerForm.controls['bannerMessage'].value;
          const learnMoreLink =
            this.marketingBannerForm.controls['linkToLearnMore'].value;

          if (
            !this.isActive &&
            (this.currentBanner == null ||
              this.currentBanner.marketingBannerID != bannerMessage ||
              this.currentBanner.linkToLearnMore != learnMoreLink)
          )
            this.updateBanner();
          else this.refreshBanner(programType.programTypeId);
        },
        () => (this.loading = false)
      );
  }

  updateBanner(): void {
    const bannerMessage =
      this.marketingBannerForm.controls['bannerMessage'].value;
    const learnMoreLink = this.marketingBannerForm.controls[
      'linkToLearnMore'
    ].value.replace(/^[^a-zA-Z]+/, '');
    const programType = this.marketingBannerForm.controls['programType'].value;

    if (
      !!bannerMessage &&
      bannerMessage.length > 0 &&
      bannerMessage.length <= 560
    ) {
      const msg = {
        description: bannerMessage,
        linkToLearnMore: learnMoreLink,
        programTypeId: programType.programTypeId
      };
      this.loading = true;
      this.osmService.updateMarketingBanner(msg).subscribe(
        () => {
          this.loading = false;
          this.refreshBanner(programType.programTypeId);
        },
        () => (this.loading = false)
      );
    }
  }

  private updateStartStopText(active: boolean) {
    this.startStopText = active ? 'Stop banner' : 'Start Banner';
  }

  get isUpdateButton(): boolean {
    const bannerMessage =
      this.marketingBannerForm.controls['bannerMessage'].value;
    const bannerLink =
      this.marketingBannerForm.controls['linkToLearnMore'].value;
    return (
      bannerMessage == this.currentBannerMsg &&
      bannerLink == this.currentBannerLink
    );
  }

  get isStartButton(): boolean {
    if (!this.isActive) {
      if (this.marketingBannerForm.controls['bannerMessage'].value.length > 0) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  }

  get getLimit(): number {
    return 32 - this.marketingBannerForm.controls['bannerMessage'].value.length;
  }

  onLoadProgramType(): void {
    this.programTypes = [
      { programTypeName: 'MCAP', programTypeId: 1 },
      { programTypeName: 'CSI', programTypeId: 2 },
      { programTypeName: 'Operator', programTypeId: 3 },
      { programTypeName: 'Events', programTypeId: 4 }
    ];
  }

  onProgramTypeChange(event: SelectChangeEvent): void {
    this.marketingBannerForm.controls['bannerMessage'].setValue('');
    this.marketingBannerForm.controls['linkToLearnMore'].setValue('');
    this.refreshBanner((event as any).value.programTypeId);
  }
}
